import type { CaseStudySection } from './amexCaseStudy';

export const hotelplannerIntro = {
  client: 'HotelPlanner',
  title: 'Redesigning an online booking experience with $1B annual volume',
  summary:
    'Led a mobile-first redesign of a hotel landing page with $1B+ in annual transaction volume, grounded in competitive analysis of leading travel sites.',
  background: 'linear-gradient(180deg, #E8F0F6 0%, #B8CCDC 100%)',
};

export const hotelplannerDesktopVideo =
  'https://cdn.magicpatterns.com/uploads/7rbdvdbUgZbQ9AzqtRNEes/hp%20hotel%20page%20desktop.mp4';

export const hotelplannerMetrics = [
  { value: '$1B+', label: 'annual volume' },
  { value: '2M', label: 'monthly visitors' },
  { value: '2,500+', label: 'SaaS users' },
];

export const hotelplannerSections: CaseStudySection[] = [
  {
    label: 'Problem',
    heading: 'A dated experience at the moment of purchase',
    paragraphs: [
      "HotelPlanner is a large online travel agency. Its booking experience looked old and didn't work well on phones, which cost trust right where it mattered most, at the point of conversion. A cleaner, easier flow was the most direct way to win back conversions that competitors were capturing.",
    ],
  },
  {
    label: 'Challenge',
    heading: 'Too much data for a small screen',
    paragraphs: [
      'Each hotel came with a huge amount of data, and I had to fit it onto a phone screen. The existing UI had been designed by developers and was overloaded with information.',
    ],
  },
  {
    label: 'Key Decisions',
    bullets: [
      "I designed mobile first, since it's the most-used form factor. The small screen forced me to prioritize ruthlessly.",
      'I showed users only the highlights and used progressive disclosure for the rest. The main page carries just enough to scan and compare, and modals hold the details.',
      'I borrowed proven patterns from competitors. Many companies have already worked on this problem, so I used their best practices to reach parity quickly rather than reinventing anything.',
    ],
  },
  {
    label: 'Approach',
    heading: 'Starting from the end state',
    paragraphs: [
      'Rather than build up gradually, I started with a mobile-optimized experience that already reflected competitor best practices. That gave the team something concrete to react to.',
    ],
  },
  {
    label: 'Results',
    heading: 'Prototype and results',
    paragraphs: [
      'I built a full prototype in Figma at desktop and mobile sizes, and later rebuilt it in Magic Patterns. The new design produced an immediate, visible lift in conversion. Channel partners responded positively, and the company has kept using the designs.',
    ],
  },
  {
    figures: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/sYUHutkHDTDysSWEc4gGoi/results_desktop.jpg',
        alt: 'HotelPlanner desktop search results with a filter sidebar, hotel cards with photos, amenities, ratings and prices, and a price map',
        caption: 'Desktop results with filters, scannable hotel cards, and a price map.',
      },
      {
        images: [
          {
            src: 'https://cdn.magicpatterns.com/uploads/ny3gCEVFQiczQeKs3vfdvb/results_mobile_1.jpg',
            alt: 'Mobile search results with the price map open above the first hotel card',
          },
          {
            src: 'https://cdn.magicpatterns.com/uploads/7K8krSCPc9EoVUkG8opnna/results_mobile_2.jpg',
            alt: 'Mobile search results with the map collapsed, showing hotel cards',
          },
          {
            src: 'https://cdn.magicpatterns.com/uploads/bxNbV92m2j2WKqMoidcSs4/results_mobile_3.jpg',
            alt: 'Mobile filters panel with sort, popular filters, price range, guest rating and star rating',
          },
        ],
        caption: 'Mobile results with a collapsible map and filters in a slide-out panel.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/62cpo9xvvzSGKQnmBNeh6Q/details_desktop.jpg',
        alt: 'HotelPlanner desktop hotel detail page with photo gallery, overview, AI insight, highlights, room options, reviews, policies, and nearby hotels',
        caption: 'Desktop hotel detail page, from photos and room options through reviews and policies.',
      },
      {
        images: [
          {
            src: 'https://cdn.magicpatterns.com/uploads/jeqfHEViTK83icpnaBmWor/detail_mobile_1.jpg',
            alt: 'Mobile hotel detail with photo carousel, overview, rating, call-to-book link, and AI insight',
          },
          {
            src: 'https://cdn.magicpatterns.com/uploads/uxYoDY348HLssVyZg8nhQo/details_mobile_2.jpg',
            alt: 'Mobile room options card with rate types, prices, and Book Now buttons',
          },
          {
            src: 'https://cdn.magicpatterns.com/uploads/tBQ7EFoJcyL48gApkjWFn7/details_mobile_3.jpg',
            alt: 'Mobile room details screen with photo carousel, rates, and an AI insight about the room',
          },
          {
            src: 'https://cdn.magicpatterns.com/uploads/myyycuNEBvMwaQBbJiLTZh/details_mobile_4.jpg',
            alt: 'Mobile reviews with rating distribution and individual guest reviews',
          },
          {
            src: 'https://cdn.magicpatterns.com/uploads/fXp87na4N5gMJevE68eyCs/details_mobile_5.jpg',
            alt: 'Mobile map screen with the hotel location and distances to nearby attractions',
          },
          {
            src: 'https://cdn.magicpatterns.com/uploads/u6T4U59J1s8Hi3BXnmGqFa/details_mobile_6.jpg',
            alt: 'Mobile list of more hotels nearby with ratings, prices, and call buttons',
          },
        ],
        caption: 'Mobile hotel detail, with room details, reviews, and the map one tap away.',
      },
    ],
  },
];
